import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { atomTimeoutTime } from "../../atoms";
import { UTILITY_SESSION_TIMEOUT_PAGE_URL } from "../../routing/routes.config";
import useApiClient from "../useApiClient";
import { ApiException } from "../useApiClient/ClientProxy.generated";

export const HTTP_STATUS_CODE_TIMEOUT = 408;
export const EXPIRY_CHECK_INTERVAL = 30000;

export const useSessionExpiry = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const client = useApiClient();
  const setTimeoutTime = useSetRecoilState(atomTimeoutTime);
  const [expireInSeconds, setExpireInSeconds] = useState<number | null>(null);
  const [wait, setWait] = useState(EXPIRY_CHECK_INTERVAL);
  const [isError, setIsError] = useState(false);

  const getNextDelay = (expiry: number) => Math.max(expiry * 500, EXPIRY_CHECK_INTERVAL);

  useEffect(() => {
    let isCancelled = false;

    const checkSessionExpiry = async () => {
      try {
        setIsError(false);
        const response = await client.getsessionexpiry();
        if (!isCancelled) {
          const expiry = parseInt(response.result);
          setTimeoutTime(new Date().getTime() + expiry * 1000);
          setWait(getNextDelay(expiry));
          setExpireInSeconds(expiry);
        }
      } catch (ex) {
        const response = ex as ApiException;
        if (response?.status === HTTP_STATUS_CODE_TIMEOUT) {
          setWait(getNextDelay(-1));
          setExpireInSeconds(-1);
          navigate(UTILITY_SESSION_TIMEOUT_PAGE_URL, {
            replace: true,
          });
        } else {
          setIsError(true);
        }
      }
    };

    const isSessionTimeoutPage = location.pathname.endsWith(HTTP_STATUS_CODE_TIMEOUT.toString());

    if (!isSessionTimeoutPage) {
      const handle = setInterval(async () => await checkSessionExpiry(), wait);
      return () => {
        isCancelled = true;
        clearTimeout(handle);
      };
    }

    return () => {
      isCancelled = true;
    };
  }, [wait, client, navigate, location.pathname, setTimeoutTime]);

  return {
    isError,
    expireInSeconds,
  };
};

export default useSessionExpiry;
